import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import * as _ from 'lodash';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientService } from './../../../../core/services/patient.service';
import { TagService } from './../../../../core/services/tag.service';

import { animate, state, style, transition, trigger } from '@angular/animations';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, Subscription, map, of, startWith, switchMap, takeUntil } from 'rxjs';
import { GroupService } from 'src/app/core/services/group.service';
import { isSurveyTag, surveyLine, withSurveyTags } from 'src/app/core/utils/survey-tags';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

@Component({
  selector: 'app-tags-list',
  templateUrl: './tags-list.component.html',
  styleUrls: ['./tags-list.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({
        height: '0',
        overflow: 'hidden',
        opacity: '0'
      })),
      state('expanded', style({
        height: '*',
        opacity: '1'
      })),
      transition('expanded <=> collapsed', animate('300ms ease-out')),
    ])
  ]
})
export class TagsListComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() filterValue: string = '';
  patients: any[] = [];
  filteredPatients: any[] = [];

  public expandedHospitalId: number | null = null;

  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;
  tagFilterControl = new FormControl();
  filteredTags?: Observable<any[]>;
  tagChips: any[] = [];
  readonly separatorKeysCodes: number[] = [13, 188]; // Enter and comma key codes
  addOnBlur = false;
  groupTags?: any[];

  private filterSub?: Subscription;

  constructor(
    private patientService: PatientService,
    private router: Router,
    private tagService: TagService,
    private groupService: GroupService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    this.tagService.tags$
      .pipe(takeUntil(this.destroy$))
      .subscribe(results => {
        this.groupTags = this.tagsFor(results);
        this.filteredTags = this.tagFilterControl.valueChanges.pipe(
          startWith(''),
          switchMap(value => this.filterTags(value))
        );
      });

    this.groupService.adjustPatientValidationStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(patientId => {
        if (patientId) {
          this.filterPatients();
        }
      });

  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filterValue']) {
      this.applyFilter();
    }
  }

  readonly isSurveyTag = isSurveyTag;

  /** The practice's tags, plus the built-in survey tags except for a state unit, which sends no surveys. */
  tagsFor(tags: string[]): string[] {
    return isStateModeGroup(this.groupService.group) ? tags : withSurveyTags(tags);
  }
  readonly surveyLine = surveyLine;

  buildDiagnosisString(patient: any): string {
    const { diagnosis2, diagnosis3, diagnosis4, diagnosis5 } = patient;
    const diagnoses = [diagnosis2, diagnosis3, diagnosis4, diagnosis5].filter(Boolean); // Filter out falsy values
    const diagnosisString = diagnoses.join(', ');
    return diagnosisString.length > 70 ? `${diagnosisString.substring(0, 70)}...` : diagnosisString;
  }

  applyFilter() {
    if (!this.patients) {
      return;
    }

    const filterTerms = this.filterValue.toLowerCase().split(' ');
    this.filteredPatients = _.filter(this.patients, (patient: any) => {

      const icdCodesMatch = patient.icdCodes?.some((icdCode: any) =>
        filterTerms.every(term =>
          _.includes((icdCode.code || '').toLowerCase(), term) ||
          _.includes((icdCode.description || '').toLowerCase(), term)
        )
      );

      const patientMatch = filterTerms.every(term =>
        _.includes(patient.name.toLowerCase(), term) ||
        _.includes(patient.surname.toLowerCase(), term) ||
        _.includes(patient.patientNumber?.toLowerCase(), term) ||
        _.includes(patient.diagnosis?.toLowerCase(), term)
      );

      return icdCodesMatch || patientMatch;
    });

  }

  private filterTags(value: string): Observable<any[]> {
    if (!this.groupTags) {
      return of([]); // Return an empty array if groupTags is not available yet
    }

    if (!value || typeof value !== 'string') {
      return of(this.groupTags); // Return groupTags when the input is empty
    }

    const filterValue = value.toLowerCase();
    return of(this.groupTags).pipe(
      map(tags => tags.filter(tag =>
        tag.toLowerCase().includes(filterValue)
      ))
    );
  }

  /* private filterTags(value: string): Observable<any[]> {
    if (!value || typeof value !== 'string') return of(this.groupTags || []);
    if (!this.groupTags?.length) return of([]);

    const filterValue = value.toLowerCase();
    return of(this.groupTags).pipe(
      map(tags => tags.filter(tag =>
        tag.toLowerCase().includes(filterValue)
      ))
    );
  } */


  resetInput(): void {
    setTimeout(() => {
      this.inputElement.nativeElement.value = '';
      this.tagFilterControl.reset();
    });
  }

  addChip(tag: any): void {
    if (!this.tagChips.includes(tag)) {
      this.tagChips.push(tag);
    }
    this.filterPatients();
    this.resetInput();
  }

  removeChip(index: number): void {
    this.tagChips.splice(index, 1);
    this.filterPatients();
  }

  filterPatients() {
    if (this.filterSub) {
      this.filterSub.unsubscribe();
    }
    this.filterSub = this.tagService.getPatientsByTag(this.tagChips)
      .pipe(takeUntil(this.destroy$))
      .subscribe(results => {
        this.patients = results.data;
        this.applyFilter();
      })
  }

  navigateToPatientFile(patientId: string) {
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }
}